import { cn } from "@/lib/utils";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
  label?: string;
}

const sizeClasses = {
  sm: "h-4 w-4 border-2",
  md: "h-6 w-6 border-2",
  lg: "h-8 w-8 border-3",
};

/**
 * Accessible loading spinner component.
 * Includes aria-label for screen readers.
 */
export function LoadingSpinner({ 
  size = "md", 
  className,
  label = "Loading" 
}: LoadingSpinnerProps) {
  return (
    <div
      role="status"
      aria-label={label}
      className={cn(
        "inline-block animate-spin rounded-full border-solid border-primary border-r-transparent",
        sizeClasses[size],
        className
      )}
    >
      <span className="sr-only">{label}</span>
    </div>
  );
}

/**
 * Full-screen loading overlay for page transitions.
 */
export function LoadingOverlay({ message = "Loading..." }: { message?: string }) {
  return (
    <div 
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center"
      role="alert"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3">
        <LoadingSpinner size="lg" label={message} />
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

/**
 * Inline loading state for buttons or small areas.
 */
export function LoadingDots({ className }: { className?: string }) {
  return (
    <span 
      className={cn("inline-flex gap-1", className)}
      role="status"
      aria-label="Loading"
    >
      <span className="animate-bounce [animation-delay:-0.3s] h-1.5 w-1.5 rounded-full bg-current" />
      <span className="animate-bounce [animation-delay:-0.15s] h-1.5 w-1.5 rounded-full bg-current" />
      <span className="animate-bounce h-1.5 w-1.5 rounded-full bg-current" />
      <span className="sr-only">Loading</span>
    </span>
  );
}
